import React from "react";
import { configureStore } from "@reduxjs/toolkit";
import { Provider, useDispatch, useSelector } from "react-redux";
import { debounce, random } from "lodash";
import { XpWarnings } from "./fire/xpHooks";
import { TapPhases } from "root/tutorial/steps/TapCard";
import { browserLang, isBrowser } from "tools/device";
import { motivationGigs } from "root/main/main/quests/misc/motivationGigs";
import { getStoragedJsVersion } from "tools/storageJsVersion";
import { TLoopFilter } from "root/main/learn/CardsFilter";
import { CardData } from "dexieDB/dexie";
import { viewerQuery$data } from "queries/__generated__/viewerQuery.graphql";
import { viewerQuery_friends$data } from "queries/__generated__/viewerQuery_friends.graphql";
import { viewerQuery_xps$data } from "queries/__generated__/viewerQuery_xps.graphql";
import { viewerQuery_decks$data } from "queries/__generated__/viewerQuery_decks.graphql";
import { StoreId } from "root/profile/subscription/subscriptionIds";
import { FeedbackMessage } from "root/profile/feedback/MessageBubbles";
import { SALE, SuggestionsTranslations } from "../sharedJs__generated/constants";

const defaultJpns = ["Hiragana", "Romaji"];

const LOGOUT = "LOGOUT";
const SET_TOKEN = "SET_TOKEN";
const SET_PLANG = "SET_PLANG";
const AD = "AD";
const XP = "XP";
const XP_BURN = "XP_BURN";
const XP_COLDNESS = "XP_COLDNESS";
const XP_TOO_MANY_WARNING_SEEN = "XP_TOO_MANY_WARNING_SEEN";
const DEVICEID = "DEVICEID";
const THEORYLANG = "THEORYLANG";
const LEARNLIMIT = "LEARNLIMIT";
const TAPPHASE_NEXT = "TAPPHASE_NEXT";
const QUESTS_ADDSRC = "QUESTS_ADDSRC";
const CAN_RECOMMEND = "CAN_RECOMMEND";
const ADD_CONNECTION_ISSUE = "ADD_CONNECTION_ISSUE";

export type CardValues = {
  id?: string;
  svg?: { flatId: string; url: string } | null;
  front: string;
  back: string;
  flipped?: boolean;
  hint?: string | null;
  theory?: string | null;
  sourceId?: string | null;
  sCardId?: string | null;
  sBackId?: string | null;
  cardEditCallback?: VoidFunction;
  noDeck?: boolean;
};
export type Theme = "default" | "dark" | "pink" | "sepia";
export type Subscriptions = { [storeId: string]: SubType };
export type SubType = {
  storeId: StoreId;
  price?: string;
  priceOrig?: string;
  priceMicros: number;
  canPurchase: boolean /*...*/;
  countryCode?: string;
  currency: string;
  currencyOrig?: string;
  isTrial: boolean;
};
export type Quests = {
  // deprecated
  addBySharing?: boolean;
  addChrome?: boolean;
};

export type SingleLangTranslationMap = {
  [key: string]: string;
};

export type AllLangsTranslationsMap = {
  [languageCode: string]: SingleLangTranslationMap;
};

export type ConnectionIssue = {
  message: string;
  time: number;
  networkData?: { [key: string]: any };
};

export type ChatIdeasSettings = {
  active?: boolean;
  withTranslation?: boolean;
};

// list of all purchase impulses which are then stored into dblog with `Confirmed purchase` name
export type LastImpulseName =
  | "ad"
  | "aiLimit"
  | "flamesLimit"
  | "cardsLimit"
  | "grammarLimit"
  | "subsKicker"
  | "sale"
  | "themeFeature"
  | "cardFilterFeature"
  | "voiceFeature"
  | "autoSpeechFeature"
  | "pickedCardsFeature"
  | "pronounceFeature"
  | "undoFeature"
  | "tooManyDecksLimit"
  | "streakRecovery"
  | "onboarding1"
  | "onboarding2"
  | "fromProfile";

export type LastImpulse = {
  name: LastImpulseName;
  timestamp: number;
};

export type State = {
  token?: string;
  releaseId: string;
  deckId?: string;
  voiceOff?: boolean;
  voiceOffUntil?: number;
  volume?: number;
  romanOff?: boolean;
  pairingOff?: boolean;
  deckCreate?: { lang: string };
  pickedLang?: string;
  routeHistory?: { pathname?: string; search?: string; hash?: string }[];
  // false => error
  subscriptions?: false | Subscriptions;
  alreadyPurchasedBy?: { email: string; transactionId: string };
  adStack: number; // 0-100 (%)
  tooManyDecks?: boolean;
  xp: {
    stack: number; // 0-100 (%)
    strength: number; // -1 frozen, 0 grey sleeping, 1-5 size of the fire
    coldness: number; // 0 no ice cube, 1-5 times 20% opacity of ice cube
    overflowed: boolean;
    lastActivity?: number;
    warn?: XpWarnings;
    tooManyWarningSeen: boolean;
    fireWarningClicked?: boolean;
  };
  courseView: {
    sourceUriOrId: string;
    langNative: string; // what language should the source be translated into
    langLearning: string; // used to determine my deck to see how far I got in the course (I can have French deck but in Library I might have selected German)
    openTheoryByCardId?: string; // if we want to override which theory should be open by default
    allowScrollAnimation?: boolean; // we show some animations only when user finishes a theory block
    canShowTooMany?: boolean; // we recommend user to go to learn only when he finishes a theory block
  };
  inviteDialog?: boolean;
  libTopic?: string;
  theme?: Theme;
  deviceId?: string;
  deviceOs?: string;
  lastVoice?: { voiceId: string; text: string };
  japAlphabeth?: string[];
  theoryLang?: { [key: string]: string };
  learnLimit?: boolean;
  tutorialTapPhase?: string;
  motivationGigIndex: number;
  viewer?: viewerQuery$data["viewer"];
  decks?: viewerQuery_decks$data["decks"];
  xps?: viewerQuery_xps$data["xps"];
  friends?: viewerQuery_friends$data["friends"];
  quests?: Quests;
  lastGrammarLang?: string;
  loopFilter?: TLoopFilter;
  loopFilterDeckId?: string;
  savedLoopFilter?: TLoopFilter;
  connectionProblem?: boolean;
  loaderGlobal?: boolean;
  lastSeenTotalXp?: number;
  pickedFewCards?: boolean;
  ownWordAdded?: boolean;
  grammarUsage?: {
    dateISO: string;
    views: number;
  };
  showQuestsBackdrop?: boolean;
  present?: boolean;
  storageSizeChecked?: number;
  needsUpdate?: boolean;
  pettingMemo?: boolean;
  canRecommend?: string[];
  questStartsOnXp?: { name: string; xp: number };
  ignoreShareAndChrome?: boolean;
  fcmId?: string | null;
  closeSale?: boolean;
  idbCardsData: CardData[];
  adminOrigin?: string;
  trackingDenied?: boolean;
  canRequestAdmobAds?: boolean;
  purchasedOffer?: { orderedId?: StoreId | null; finishedId?: StoreId | null };
  boughtAlready?: StoreId;
  langTranslates?: AllLangsTranslationsMap;
  orderOpened?: number;
  bottomCardActive?: boolean;
  rightSwipesCount?: number;
  suggestionsTranslations?: { [lang: string]: SuggestionsTranslations };
  showSentenceHint?: boolean;
  recentConnectionIssues?: ConnectionIssue[];
  noCardsDialogShown?: string;
  lastQuestXps?: number;
  questAiAssistant?: boolean;
  questListenVocab?: boolean;
  isAudioRecording?: boolean;
  logUIActive?: boolean;
  onboardingFeaturesScreenIndex?: number;
  deviceUsersDialogOpen?: boolean;
  assistantAutoSpeechOff?: boolean;
  currentQuest?: { name: string; startXps: number };
  lastFeedback?: {
    relatedTo: string | undefined;
    messages: FeedbackMessage[];
    createdAt: number;
    helpfulAnswer?: boolean;
  };
  saleMainDialogLastSeen?: SALE;
  loggedByHash?: boolean;
  micLastUsed?: number;
  imagesOffUntil?: number;
  outage?: string;
  appUpdateNoticeLastShown?: number;
  listenVocabSettings?: {
    withTranslation?: boolean;
  };
  chatIdeasSettings?: ChatIdeasSettings;
  lastImpulse?: LastImpulse;
  speakingStarterIndex?: number;
  linkedSource?: { id: string; title: string; uri: string; lang: string };
};

const persisted = localStorage.getItem("reduxState");
const fromStorage: State = persisted ? JSON.parse(persisted) : { pickedLang: browserLang() }; // setting default pickedLang only to new users who don't have redux in storage yet, we don't want to switch lang for existing users due to historical reasons
const initState: State = {
  adStack: fromStorage.token ? 40 : 0,
  xp: {
    stack: 0,
    strength: 0,
    coldness: 0,
    overflowed: false,
    tooManyWarningSeen: false
  },
  courseView: {
    sourceUriOrId: "",
    langNative: "",
    langLearning: ""
  },
  idbCardsData: [],
  motivationGigIndex: random(0, motivationGigs.length - 1),
  releaseId: getStoragedJsVersion()
};

// Generic selector
// NOTE: when using, define generic type as State['{stateName}']

export const useGetState = <K extends keyof State>(stateName: K) => {
  return useSelector((state: State) => state[stateName]);
};

// Complex selectors with special logic
export const selectTheme = (state: State) => state.theme || "default";
export const selectJapAlphabeth = (state: State) =>
  state.japAlphabeth === undefined ? defaultJpns : state.japAlphabeth;
export const selectCanRecommend = (lang: string | undefined) => (state: State) =>
  state.canRecommend?.some((l) => l === lang);

// Generic setter
// NOTE: when using, define generic type as State['{stateName}']
export const useSetState = <K extends keyof State>(stateName: K) => {
  const dispatch = useDispatch();
  return React.useCallback((payload: State[K]) => dispatch({ type: stateName, payload }), [dispatch, stateName]);
};

export const useReduxState = <K extends keyof State>(stateName: K): [State[K], (payload: State[K]) => void] => {
  const state = useGetState(stateName);
  const setState = useSetState(stateName);
  return [state, setState];
};

// Complex action creators with custom logic
export const logout = () => ({ type: LOGOUT });
export const incAdStack = (payload: number | null) => ({ type: AD, payload });
export const setXp = (payload: State["xp"]) => ({ type: XP, payload });
export const setXp_burn = (payload: { warn?: XpWarnings }) => ({ type: XP_BURN, payload });
export const setXpColdness = (payload: number) => ({ type: XP_COLDNESS, payload });
export const setXpTooManyWarningSeen = () => ({ type: XP_TOO_MANY_WARNING_SEEN });
export const setTheoryLang = (payload: { forLang: string; pickedLang: string }) => ({
  type: THEORYLANG,
  payload
});
export const nextTutorialTapPhase = () => ({ type: TAPPHASE_NEXT });
export const setQuestAddSource = (name: keyof Quests) => ({ type: QUESTS_ADDSRC, payload: { name } });
export const setCanRecommend = (lang: string, canRecommend: boolean) => ({
  type: CAN_RECOMMEND,
  payload: { lang, canRecommend }
});
export const addConnectionIssue = (message: string, networkData?: { [key: string]: any }) => ({
  type: ADD_CONNECTION_ISSUE,
  payload: { message, networkData, time: Date.now() }
});

const rootReducer = (
  state: State = { ...fromStorage, ...initState },
  action: { type: string; payload: any }
): State => {
  switch (action.type) {
    case SET_TOKEN:
      return { ...state, token: action.payload };
    case SET_PLANG:
      return { ...state, pickedLang: action.payload };
    case AD:
      return { ...state, adStack: action.payload ? state.adStack + action.payload : 0 };
    case XP:
      return { ...state, xp: action.payload };
    case XP_COLDNESS:
      return {
        ...state,
        xp: {
          ...state.xp,
          coldness: action.payload > 5 ? 5 : action.payload < 0 ? 0 : action.payload
        }
      };
    case XP_BURN:
      let strength: number;
      let coldness: number;
      let stack = state.xp.stack;
      if (action.payload.warn) {
        strength = state.xp.strength > 1 ? state.xp.strength - 1 : 1;
        coldness = state.xp.coldness < 5 ? state.xp.coldness + 1 : 5;
        if (state.xp.coldness < 5 && coldness === 5) {
          stack = stack >= 20 ? stack - 20 : 0;
        }
      } else {
        coldness = 0;
        strength = state.xp.strength < 5 ? state.xp.strength + 1 : 5;
        strength === 0 && strength++; // skip 0
      }

      return {
        ...state,
        xp: {
          ...state.xp,
          warn: action.payload.warn,
          lastActivity: Date.now(),
          strength,
          stack,
          coldness
        }
      };
    case XP_TOO_MANY_WARNING_SEEN:
      return {
        ...state,
        xp: {
          ...state.xp,
          tooManyWarningSeen: true
        }
      };
    case DEVICEID:
      return { ...state, deviceId: action.payload };
    case THEORYLANG:
      return {
        ...state,
        theoryLang: { ...state.theoryLang, [action.payload.forLang]: action.payload.pickedLang }
      };
    case LEARNLIMIT:
      return { ...state, learnLimit: action.payload };
    case TAPPHASE_NEXT:
      const phaseIndex = TapPhases.findIndex((p) => p === state.tutorialTapPhase);
      return { ...state, tutorialTapPhase: TapPhases[phaseIndex + 1] || state.tutorialTapPhase };
    case QUESTS_ADDSRC:
      return {
        ...state,
        quests: { ...state.quests, [action.payload.name]: true }
      };
    case CAN_RECOMMEND:
      const can = action.payload.canRecommend;
      const is = !!state.canRecommend?.includes(action.payload.lang);
      if (can === is) return state;
      return {
        ...state,
        canRecommend: can
          ? [...(state.canRecommend || []), action.payload.lang]
          : state.canRecommend?.filter((l) => l !== action.payload.lang)
      };
    case LOGOUT:
      // states which do persist logout
      return {
        ...initState,
        adminOrigin: undefined,
        purchasedOffer: state.purchasedOffer,
        pickedLang: state.pickedLang,
        subscriptions: state.subscriptions,
        deviceId: isBrowser() ? undefined : state.deviceId,
        grammarUsage: state.grammarUsage,
        fcmId: state.fcmId,
        trackingDenied: state.trackingDenied,
        canRequestAdmobAds: state.canRequestAdmobAds,
        boughtAlready: state.boughtAlready,
        langTranslates: state.langTranslates,
        saleMainDialogLastSeen: state.saleMainDialogLastSeen,
        logUIActive: state.logUIActive
      };
    case ADD_CONNECTION_ISSUE:
      return {
        ...state,
        recentConnectionIssues: [...(state.recentConnectionIssues || []), action.payload].slice(-10) // keep only last 10 issues
      };

    // by default saves the payload into action.type directly
    default:
      return { ...state, [action.type]: action.payload };
  }

  // TODO: update all non-specific reducers to use default branch
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["subscriptions"],
        ignoredPaths: ["subscriptions"]
      }
    })
});

const debouncedLocalStoreUpdate = debounce(() => {
  if (!document.hasFocus()) return;
  const actualState = store.getState();
  // states which do not persist sessions
  const state: State = {
    ...actualState,
    deckCreate: undefined,
    routeHistory: undefined,
    // adStack - INITIALIZED
    tooManyDecks: undefined,
    // xp - INITIALIZED
    inviteDialog: undefined,
    learnLimit: undefined,
    tutorialTapPhase: undefined,
    // motivationGigIndex: - INITIALIZED
    courseView: initState.courseView,
    connectionProblem: undefined,
    loaderGlobal: undefined,
    pickedFewCards: undefined,
    loopFilter: undefined,
    loopFilterDeckId: undefined,
    showQuestsBackdrop: undefined,
    bottomCardActive: undefined,
    pettingMemo: undefined,
    ignoreShareAndChrome: undefined,
    needsUpdate: undefined,
    fcmId: undefined,
    lastVoice: undefined,
    suggestionsTranslations: undefined,
    idbCardsData: [],
    // store only the finishedId over sessions because on session start is triggering handleVerifiedOrder()
    purchasedOffer: actualState.purchasedOffer?.finishedId
      ? { finishedId: actualState.purchasedOffer.finishedId }
      : undefined,
    orderOpened: undefined,
    showSentenceHint: undefined,
    recentConnectionIssues: undefined,
    onboardingFeaturesScreenIndex: undefined,
    deviceUsersDialogOpen: undefined
  };
  localStorage.setItem("reduxState", JSON.stringify(state));
});

store.subscribe(debouncedLocalStoreUpdate);

const ReduxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <Provider store={store}>{children}</Provider>;
};

export default ReduxProvider;
